import React, { useCallback, useEffect, useState } from 'react';
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { loginUser } from '../../slices/authSlice';
import { Link, useNavigate } from 'react-router-dom';
import { showErrorMessage, showPromiseNotification, showSuccessMessage } from '../../common/notification';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { auth, firebaseMessaging } from '../../firebase/firebaseConfig';
import { getToken } from "firebase/messaging";
import { GoogleAuthProvider, signInWithPopup, OAuthProvider } from 'firebase/auth';
import { useTranslation } from 'react-i18next';
import '../../i18';
import { encryption } from '../../common/enc_dec';

const Signin = () => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [activeTab, setActiveTab] = useState('mobile');
  const [deviceToken, setDeviceToken] = useState("");
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [phone, setPhone] = useState('91');

  useEffect(() => {
    // Load Facebook SDK
    const loadFacebookSDK = () => {
      return new Promise((resolve) => {
        if (window.FB) {
          resolve();
          return;
        }

        window.fbAsyncInit = function () {
          window.FB.init({
            appId: process.env.REACT_APP_FACEBOOK_APP_ID,
            cookie: true,
            xfbml: true,
            version: 'v19.0'
          });
          resolve();
        };

        // Load Facebook SDK script
        const script = document.createElement('script');
        script.src = 'https://connect.facebook.net/en_US/sdk.js';
        script.async = true;
        script.defer = true;
        script.crossOrigin = 'anonymous';
        document.head.appendChild(script);
      });
    };

    loadFacebookSDK();
  }, []);

  useEffect(() => {
    const fetchDeviceToken = async () => {
      try {
        const permission = await Notification.requestPermission();
        if (permission === "granted") {
          const token = await getToken(firebaseMessaging, {
            vapidKey: process.env.REACT_APP_VAPID_KEY,
            serviceWorkerRegistration: await navigator.serviceWorker.register('/firebase-messaging-sw.js')
          });
          if (token) {
            setDeviceToken(token);
          } else {
            console.warn("No device token retrieved. Ensure your app is correctly configured.");
          }
        } else {
          console.warn("Notification permission denied.");
        }
      } catch (err) {
        console.error("Error retrieving device token:", err);
      }
    };

    fetchDeviceToken();
  }, []);

  const signinSchema = Yup.object().shape({
    email: activeTab === 'email'
      ? Yup.string()
        .required(t('required', { field: t("email") }))
        .email(t('valid', { field: t("email") }))
      : Yup.string(),
    phone_number: activeTab === 'mobile'
      ? Yup.string()
        .required(t('required', { field: t("phone_number") }))
        .matches(
          /^(\+\d{1,3}[- ]?)?\d{10}$/,
          t('valid', { field: t("phone_number") })
        )
      : Yup.string(),
    password: Yup.string()
      .required(t('required', { field: t("password") }))
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
        t('passwordValidation')
      ),
  });

  const formOptions = { resolver: yupResolver(signinSchema) };
  const { register, handleSubmit, formState, setValue } = useForm(formOptions);
  const { errors } = formState;

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // Clear the password field when tab changes
    setValue('password', '');
    // Also clear the other field values when switching tabs
    if (tab === 'mobile') {
      setValue('email', '');
    } else {
      setValue('phone_number', '');
    }
  };

  // Helper function to navigate to signup with data
  const navigateToSignup = (userData) => {
    const queryString = encryption(userData, true);
    navigate(`/sign-up?data=${encodeURIComponent(queryString)}`);
  };

  const handleGoogleSignIn = async (login_type) => {
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      const submissionData = {
        email: auth.currentUser.email,
        social_id: auth.currentUser.uid,
        login_type: 'G',
        device_token: deviceToken,
        device_type: "W"
      }

      const response = await dispatch(
        loginUser({
          payload: {
            ...submissionData
          }
        })
      )
      if (response.payload.code == 200) {
        if (response.payload.data.step === 1) {
          showSuccessMessage(response.payload.message)
          navigate("/complete-profile")
        }
        else {
          showSuccessMessage(response.payload.message);
          navigate("/home");
        }
      }
      else if (response.payload.code == 404) {
        // showErrorMessage("Account not found. Redirecting to sign-up...");
        // Pass Google user data to signup
        navigateToSignup({
          name: auth.currentUser.displayName,
          email: auth.currentUser.email,
          social_id: auth.currentUser.uid,
          login_type: 'G'
        });
      }
      else {
        showErrorMessage(response.payload.message)
      }
    }
    catch (err) {
      if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') return;
      showErrorMessage(t("errorMsg"));
    }
  }

  const handleFacebookSignIn = async () => {
    try {
      if (!window.FB) {
        showErrorMessage('Facebook SDK not loaded');
        return;
      }

      // Check login status first
      window.FB.getLoginStatus((response) => {
        if (response.status === 'connected') {
          // User is already logged in, get their info
          getFacebookUserInfo(response.authResponse.accessToken);
        } else {
          // User needs to login
          window.FB.login((loginResponse) => {
            if (loginResponse.authResponse) {
              getFacebookUserInfo(loginResponse.authResponse.accessToken);
            } else {
              console.log('User cancelled login or did not fully authorize.');
            }
          }, {
            scope: 'email,public_profile'
          });
        }
      });

    } catch (err) {
      console.error('Facebook login error:', err);
      showErrorMessage(t('errorMsg'));
    }
  }

  // Get Facebook user information and proceed with login
  const getFacebookUserInfo = (accessToken) => {
    window.FB.api('/me', {
      fields: 'id,name,email,picture.type(large)'
    }, async (response) => {
      if (response && !response.error) {
        const submissionData = {
          email: response.email,
          social_id: response.id,
          login_type: 'F',
          device_token: deviceToken,
          device_type: "W"
        };

        const loginResponse = await dispatch(
          loginUser({
            payload: {
              ...submissionData
            }
          })
        )
        if (loginResponse.payload.code == 200) {
          if (loginResponse.payload.data.step === 1) {
            showSuccessMessage(loginResponse.payload.message)
            navigate("/complete-profile")
          }
          else {
            showSuccessMessage(loginResponse.payload.message);
            navigate("/home");
          }
        }
        else if (loginResponse.payload.code == 404) {
          // showErrorMessage("Account not found. Redirecting to sign-up...");
          // Pass Facebook user data to signup
          navigateToSignup({
            name: response.name,
            email: response.email,
            social_id: response.id,
            login_type: 'F',
            picture: response.picture?.data?.url || null,
            access_token: accessToken
          });
        }
        else {
          showErrorMessage(loginResponse.payload.message)
        }
      } else {
        console.error('Error getting user info:', response.error);
        showErrorMessage('Failed to get user information from Facebook');
      }
    });
  }

  const handleAppleSignIn = async () => {
    try {
      const provider = new OAuthProvider('apple.com');
      await signInWithPopup(auth, provider);
      const submissionData = {
        email: auth.currentUser.email,
        social_id: auth.currentUser.uid,
        login_type: 'A',
        device_token: deviceToken,
        device_type: "W"
      }

      const response = await dispatch(
        loginUser({
          payload: {
            ...submissionData
          }
        })
      )
      if (response.payload.code == 200) {
        if (response.payload.data.step === 1) {
          showSuccessMessage(response.payload.message)
          navigate("/complete-profile")
        }
        else {
          showSuccessMessage(response.payload.message);
          navigate("/home");
        }
      }
      else if (response.payload.code == 404) {
        // showErrorMessage("Account not found. Redirecting to sign-up...");
        // Pass Apple user data to signup
        navigateToSignup({
          name: auth.currentUser.displayName,
          email: auth.currentUser.email,
          social_id: auth.currentUser.uid,
          login_type: 'A'
        });
      }
      else {
        showErrorMessage(response.payload.message)
      }
    }
    catch (err) {
      if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') return;
      showErrorMessage("Something went wrong! Please try again.");
    }
  }

  const onSubmit = useCallback(
    async (values) => {
      const submissionData = activeTab === 'mobile'
        ? { phone_number: values.phone_number, password: values.password, login_type: 'S', country_code: phone, device_type: "W" }
        : { email: values.email, password: values.password, login_type: 'S', device_type: "W" };

      submissionData.device_token = deviceToken;
      const myPromise = new Promise((resolve, reject) => {
        dispatch(
          loginUser({
            payload: {
              ...submissionData,
            },
          })
        )
          .then(async (response) => {
            if (response.payload.code == 200) {
              if (response.payload.data.step === 1) {
                resolve(response.payload);
                navigate("/complete-profile");
              }
              else {
                resolve(response.payload);
                navigate("/home");
              }
            }
            else if (response.payload.code == 404) {
              reject(response.payload);
              // Pass simple login data to signup
              const signupData = activeTab === 'mobile'
                ? {
                  phone_number: values.phone_number,
                  country_code: phone,
                  login_type: 'S'
                }
                : {
                  email: values.email,
                  login_type: 'S'
                };
              navigateToSignup(signupData);
            }
            else {
              reject(response.payload)
            }
          });
      });

      showPromiseNotification(myPromise);
    },
    [dispatch, activeTab, navigate, phone, deviceToken]
  );

  return (
    <div className="signin-page-area" style={{ paddingTop: "100px" }}>
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-xl-6 col-lg-7">
            <form className="signin-inner" onSubmit={handleSubmit(onSubmit)}>
              <div className="row">
                <div className="col-12">
                  <div className="single-input-inner style-bg-border">
                    <h4 className="SignInUpHeading">{t('signIn')}</h4>
                  </div>
                </div>
                <div className="col-12">
                  <div className="WebsiteNavMainBox">
                    <div className="WebsiteNavBox">
                      <ul className="nav nav-tabs" id="myTab" role="tablist">
                        <li className="nav-item" role="presentation">
                          <button
                            className={`nav-link ${activeTab === 'mobile' ? 'active text-white' : ''} text-black`}
                            type="button"
                            onClick={() => handleTabChange('mobile')}
                            aria-selected={activeTab === 'mobile'}
                          >
                            {t('mobile')}
                          </button>
                        </li>
                        <li className="nav-item" role="presentation">
                          <button
                            className={`nav-link ${activeTab === 'email' ? 'active text-white' : ''} text-black`}
                            type="button"
                            onClick={() => handleTabChange('email')}
                            aria-selected={activeTab === 'email'}
                          >
                            {t('Email')}
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div className="col-12">
                  <div className="tab-content" id="myTabContent">
                    {activeTab === 'mobile' && (
                      <div
                        className="tab-pane fade show active"
                        role="tabpanel"
                        tabIndex={0}
                      >
                        <div className="TabContectMain">
                          <div className="single-input-inner style-bg-border">
                            <div className="single-input-inner style-bg-border" style={{ display: 'flex', gap: '5px', flexDirection: 'column' }}>
                              <div style={{ display: 'flex', gap: '5px' }}>
                                <PhoneInput
                                  country={'in'}
                                  value={phone}
                                  onChange={(phone, data) => {
                                    setPhone(data.dialCode)
                                  }}
                                  inputProps={{
                                    name: 'phone',
                                    required: true,
                                    autoFocus: true,
                                    disabled: true
                                  }}
                                  containerStyle={{ width: '150px', height: '50px' }}
                                  inputStyle={{ width: '100%', height: '100%' }}
                                />
                                <input
                                  type="number"
                                  placeholder={t('enterFieldName', { field: t("Phone_Number") })}
                                  name='phone_number'
                                  {...register('phone_number')}
                                  onChange={(e) => {
                                    const inputValue = e.target.value;
                                    if (inputValue.length > 10) {
                                      e.target.value = inputValue.slice(0, 10);
                                    }

                                    if (inputValue.length <= 10) {
                                      e.target.value = inputValue;
                                    }
                                  }}
                                />
                              </div>
                              <p className="text-danger" style={{ display: 'block', marginTop: '5px' }}>
                                {errors.phone_number?.message}
                              </p>
                            </div>
                          </div>
                          <div className="changeInput-box single-input-inner">
                            <div className="loginPasswordInputBox input-group">
                              <input
                                type={showPassword ? "text" : "password"}
                                name="mobilePassword"
                                className="form-control"
                                placeholder={t('enterFieldName', { field: t("Password") })}
                                {...register('password')}
                                autoComplete="off"
                              />
                              <span
                                className="input-group-text" onClick={() => setShowPassword(!showPassword)}
                              >
                                <img
                                  src={showPassword ? process.env.PUBLIC_URL + "/assets/img/icon/open-eye.svg" : process.env.PUBLIC_URL + "/assets/img/icon/close-eye.svg"}
                                  alt={showPassword ? "Close-eye" : "Open-eye"}
                                  className={showPassword ? "CloseEye" : "OpenEye"}
                                />
                              </span>
                            </div>
                            <p className="text-danger">
                              {errors.password?.message}
                            </p>
                          </div>
                          <button className="btn btn-base w-100 mb-4">{t('signIn')}</button>
                          <div className="col-12">
                            <Link to={`/forgot-password/${activeTab}`}>
                              <a>{t('forgetPassword')}</a>
                            </Link>
                          </div>
                          <div className='social-btns mt-3'>
                            <div className='d-flex justify-content-center gap-5'>
                              <div className='google-btn'>
                                <button className="btn-social btn-google" type='button' onClick={handleGoogleSignIn}>
                                  <i className="fa fa-google"></i>
                                </button>
                              </div>
                              <div className='facebook-btn'>
                                <button className="btn-social btn-facebook" type='button' onClick={handleFacebookSignIn}>
                                  <i className="fa fa-facebook"></i>
                                </button>
                              </div>
                              <div className='apple-btn'>
                                <button className="btn-social btn-apple" type='button' onClick={handleAppleSignIn}>
                                  <i className="fa fa-apple"></i>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {activeTab === 'email' && (
                      <div
                        className="tab-pane fade show active"
                        role="tabpanel"
                        tabIndex={0}
                      >
                        <div className="TabContectMain">
                          <div className="single-input-inner style-bg-border">
                            <input
                              type="text"
                              name="email"
                              placeholder={t('enterFieldName', { field: t("Email_Address") })}
                              {...register('email')}
                            />
                            <p className="text-danger">
                              {errors.email?.message}
                            </p>
                          </div>
                          <div className="changeInput-box single-input-inner">
                            <div className="loginPasswordInputBox input-group">
                              <input
                                type={showPassword ? "text" : "password"}
                                name="emailPassword"
                                className="form-control"
                                placeholder={t('enterFieldName', { field: t("Password") })}
                                {...register('password')}
                                autoComplete="off"
                              />
                              <span
                                className="input-group-text" onClick={() => setShowPassword(!showPassword)}
                              >
                                <img
                                  src={showPassword ? process.env.PUBLIC_URL + "/assets/img/icon/open-eye.svg" : process.env.PUBLIC_URL + "/assets/img/icon/close-eye.svg"}
                                  alt={showPassword ? "Close-eye" : "Open-eye"}
                                  className={showPassword ? "CloseEye" : "OpenEye"}
                                />
                              </span>
                            </div>
                            <p className="text-danger">
                              {errors.password?.message}
                            </p>
                          </div>
                          <button type='submit' className="btn btn-base w-100 mb-4">{t('signInSubmit')}</button>
                          <div className="col-12">
                            <Link to={`/forgot-password/${activeTab}`}>{t('forgetPassword')}</Link>
                          </div>
                          <div className='social-btns mt-3'>
                            <div className='d-flex justify-content-center gap-5'>
                              <div className='google-btn'>
                                <button className="btn-social btn-google" type='button' onClick={handleGoogleSignIn}>
                                  <i className="fa fa-google"></i>
                                </button>
                              </div>
                              <div className='facebook-btn'>
                                <button className="btn-social btn-facebook" type='button' onClick={handleFacebookSignIn}>
                                  <i className="fa fa-facebook"></i>
                                </button>
                              </div>
                              <div className='apple-btn'>
                                <button className="btn-social btn-apple" type='button' onClick={handleAppleSignIn}>
                                  <i className="fa fa-apple"></i>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-3 text-center">
                <span className='m-1'>{t('dontHaveAcc')}</span>
                <Link to="/sign-up"><strong>{t('createAcc')}</strong></Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Signin;